// src/pages/Products.js - Improved version matching Figma
import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Button from '../components/ui/Button';
import ProductCard from '../components/ui/ProductCard';
import { homepageAPI } from '../services/api';
import image1 from '../assets/images/product/image-1.jpg';
import image2 from '../assets/images/product/image-2.jpg';
import image3 from '../assets/images/product/image-3.jpg';
import image4 from '../assets/images/product/image-4.jpg';
import image5 from '../assets/images/product/image-5.jpg';
import image6 from '../assets/images/product/image-6.jpg';
import image7 from '../assets/images/product/image-7.jpg';
import image8 from '../assets/images/product/image-8.jpg';
import image9 from '../assets/images/product/image-9.jpg';
import image10 from '../assets/images/product/image-10.jpg';
import image12 from'../assets/images/homepage/bottom.jpg';

import { Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${API_BASE_URL}${imagePath}`;
};

const Products = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const createSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  // Fallback product categories
  const fallbackProducts = [
    {
      title: "Bread Improvers & Dough Conditioners",
      description: "Boost roller boxes, softer crumbs, and longer-lasting freshness—with zero guesswork.",
      itemCount: 12,
      image: image2
    },
    {
      title: "Premixes & Concentrates",
      description: "Simplify your process and ensure consistent results—every single time.",
      itemCount: 14,
      image: image3
    },
    {
      title: "Shelf-Life Enhancers & Preservatives",
      description: "Keep your baked goods fresher, safer, and quality-assured for longer.",
      itemCount: 10,
      image: image4
    },
    {
      title: "Emulsifiers & Texture Improvers",
      description: "Smoother texture, softer crumbs, longer shelf life—made effortless.",
      itemCount: 8,
      image: image5
    },
    {
      title: "Leavening Agents (Raising Agents)",
      description: "Rise to new heights—perfect volume and texture every time.",
      itemCount: 13,
      image: image6
    },
    {
      title: "Specialty Enzymes",
      description: "Transform your dough quality and reduce dependency on chemical additives.",
      itemCount: 17,
      image: image7
    },
    {
      title: "Other Ingredients: Starches, Gums, Flavors",
      description: "The finishing touch—functional bakery ingredients that enhance texture, flavor.",
      itemCount: 20,
      image: image8
    },
    {
      title: "Quality Assurance",
      description: "Consistent Quality. Guaranteed. Every Batch. Every Time.",
      itemCount: null,
      image: image9
    }
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await homepageAPI.getProducts();
        if (data && data.length > 0) {
          // Show backend products with proper image URLs
          setProducts(data.map(product => ({
            ...product,
            image: getFullImageUrl(product.image),
            itemCount: product.item_count,
            link: `/products/${createSlug(product.title)}`
          })));
        } else {
          // Show fallback products if no backend data
          setProducts(fallbackProducts.map(product => ({
            ...product,
            link: `/products/${createSlug(product.title)}`
          })));
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching products:', err);
        setProducts(fallbackProducts.map(product => ({
          ...product,
          link: `/products/${createSlug(product.title)}`
        })));
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = products.filter(product =>
    product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white lg:bg-cream">
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-white lg:bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Images Grid */}
            <div className="flex-1 grid grid-cols-2 gap-4 max-w-xl">
              <div className="col-span-1 row-span-2">
                <img 
                  src={image1} 
                  alt="Product 1" 
                  className="w-full h-full object-cover rounded-2xl shadow-lg"
                />
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=300&h=200&fit=crop" 
                  alt="Product 2" 
                  className="w-full h-full object-cover rounded-2xl shadow-lg"
                />
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1517433670267-08bbd4be890f?w=300&h=200&fit=crop" 
                  alt="Product 3" 
                  className="w-full h-full object-cover rounded-2xl shadow-lg"
                />
              </div>
            </div>

            {/* Text Content */}
            <div className="flex-1 max-w-xl">
              <div className="text-sm font-semibold text-primary mb-3 uppercase tracking-wide">
                Our Product Range
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold  text-text-primary mb-6 leading-tight">
                Your Perfect Bake Starts Right Here!
              </h1>
              <p className=" text-text-primary mb-6 text-lg leading-relaxed">
                Enessa Foods offers a comprehensive portfolio of bakery ingredients and mixes designed to meet the needs of modern bakers. From improving the volume of your loaf to extending the shelf life of your cakes, our products cover every aspect of baking. All our offerings are developed with a focus on quality, innovation and the demands of the Indian market – ensuring your baked goods stand out for their taste and consistency.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button variant="primary" icon="→"><a href="/contact">Contact Us</a></Button>
                <Button variant="secondary" icon="↓"><a href="#products-category">View Products</a></Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Category Section */}
      <section id="products-category" className="py-16 bg-white lg:bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-sm font-semibold text-primary mb-3 uppercase tracking-wide">
              Product Categories
            </div>
            <h2 className="text-3xl font-bold  text-text-primary mb-6">
              Explore Our Product Portfolio
            </h2>
            <p className=" text-text-primary max-w-3xl mx-auto mb-8">
              An overview of our specialized categories—crafted to meet every baker's need with performance and precision.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search in categories, products & items"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 pr-32 rounded-full border-2 border-brown/10 focus:border-primary focus:outline-none text-brown placeholder-brown-light/50 bg-white"
                />
                <button className="absolute right-2 top-1/2 -translate-y-1/2 bg-primary text-white px-6 py-2 rounded-full hover:bg-primary-dark transition-colors font-medium">
                  Search
                </button>
              </div>
            </div>
          </div>

          {/* Product Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading ? (
              Array.from({ length: 8 }).map((_, idx) => (
                <div key={idx} className="animate-pulse">
                  <div className="h-48 bg-gray-200 rounded-2xl mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-16 bg-gray-200 rounded"></div>
                </div>
              ))
            ) : (
              filteredProducts.map((product, index) => (
                <ProductCard
                  key={product.id || index}
                  title={product.title}
                  description={product.description}
                  itemCount={product.itemCount}
                  image={product.image}
                  link={product.link}
                  category={product.category || "PRODUCT CATEGORY"}
                />
              ))
            )}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-brown-light text-lg">No products found matching your search.</p>
            </div>
          )}
        </div>
      </section>

       {/* Final CTA with Chef */}
      <section className="py-24 lg:py-40 bg-white lg:bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-[40px] p-12 lg:p-24 shadow-soft flex flex-col lg:flex-row-reverse items-center gap-16">
            <div className="flex-1">
              <img src={image12} alt="Chef" className="w-full rounded-3xl shadow-2xl" />
            </div>
            <div className="flex-1 text-center lg:text-left">
              <h2 className="font-heading text-heading-lg text-text-primary mb-8 leading-tight">
               Need the Right Ingredient?<br className="hidden lg:block" /> Let's Talk!
              </h2>
              <p className="font-body text-body-lg text-text-primary mb-10 leading-normal">
                Whether you're scaling production or testing a new recipe, our experts are ready to guide you. Get free samples, technical insights, or a complete product catalog tailored to your needs.
              </p>
              <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                <Link to="/contact"><Button variant="primary" size="large">Contact Us</Button></Link>
                <Link to="/contact#inquiry-section"><Button variant="secondary" size="large">Enquiry Form</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Products;